import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Text } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { HappyOysterModel, firstFrameImageIssue } from '@reactor-models/happy-oyster';
import { api } from '../services/api';
import { captureSurface, flatFrame } from '../services/portalCapture';
import { makePortalMaterial, KEY_PRESETS, SHAPES } from '../services/portalMaterial';
import { loadTexture } from '../services/textureLoad';
import { authUser, SLICE_DISTRICT } from '../config';
import { useWorldTime } from '../worldTime';

/**
 * PortalSurface — PROOF OF CONCEPT: a wall that opens onto somewhere else.
 *
 * Reuses the placement tool wholesale. A portal is just a saved placement whose
 * content ref reads `portal:<destination>` — so you shape and position it with
 * the existing Ad-placements tool (crosshair snaps to the wall normal, drag the
 * size), and the destination is the text you'd otherwise put a content URL in.
 * No new admin surface, no schema change.
 *
 * The sequence, and why each step is the way it is:
 *
 *   1. CAPTURE the exact patch of wall the quad covers — an orthographic render
 *      framed to the quad (services/portalCapture). Exactness is the concept:
 *      frame 0 has to line up with the wall pixel-for-pixel.
 *   2. POST it to /api/reactor-helios/portal, which parks it in GCS, charges the
 *      metered minute and mints a Reactor JWT.
 *   3. Open a Happy Oyster DIRECTING session with that image as
 *      first_frame_image_url. Directing uses the image DIRECTLY as frame 0;
 *      Adventure would regenerate from it as a mere anchor and the alignment —
 *      the whole effect — would be gone.
 *   4. Stream it back onto the same quad as a VideoTexture, cross-fading from
 *      the wall so the swap has no pop.
 *
 * Happy Oyster streams off its own gateway into an HTMLVideoElement rather than
 * over Reactor's WebRTC, which suits us here: we make that <video> ourselves,
 * keep it out of the document flow, and hand it to a THREE.VideoTexture — the
 * same trick the placement layer already uses for its animated creatives.
 */

// A placement is a portal when its content ref says so; everything after the
// prefix is the destination prompt, authored by whoever placed it.
// Two prefixes, two experiments on the same placement geometry:
//   portal:    <destination>  — seed mode: the wall itself is frame 0
//   portalkey: <subject>      — chroma mode: flat key colour, keyed out here
// ?portalkey=1 forces every portal into chroma mode, so the two can be A/B'd on
// the same surface without editing placements between runs.
export const PORTAL_PREFIX = 'portal:';
export const PORTAL_KEY_PREFIX = 'portalkey:';
const ref = (p) => String(p?.default_content_ref || '').trim();
export const isPortal = (p) => {
  const r = ref(p).toLowerCase();
  return r.startsWith(PORTAL_PREFIX) || r.startsWith(PORTAL_KEY_PREFIX);
};
const qp = (k, d) => {
  const v = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get(k);
  if (v === null || v === false) return d;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : v;
};
export const portalMode = (p) =>
  (ref(p).toLowerCase().startsWith(PORTAL_KEY_PREFIX) || qp('portalkey', 0) === 1) ? 'chroma' : 'seed';
export const portalDestination = (p) => {
  const r = ref(p);
  const cut = r.toLowerCase().startsWith(PORTAL_KEY_PREFIX) ? PORTAL_KEY_PREFIX.length : PORTAL_PREFIX.length;
  return r.slice(cut).trim();
};

const FADE_SEC = 1.4;
const WHITE = new THREE.Color(1, 1, 1);
// Scratch colours for the per-frame grade; never allocate inside useFrame.
const TMP = new THREE.Color();
const SHADE = new THREE.Color();

// LIVE TUNING. Seeded from the URL, then pushed into the uniforms every frame —
// so these can be changed from the console mid-clip:
//
//    __portal.satFloor = 0.35
//    __portal.debugKey = 0
//
// This matters because a key can only honestly be judged against moving footage,
// and every reload costs a fresh prepaid minute. Reading them once at material
// build time meant paying ~8 credits per guess.
const q = (k, d) => {
  const v = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get(k);
  if (v === null || v === false) return d;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : d;
};
export const TUNING = {
  similarity: q('keysim', 0.4),
  satFloor: q('keysat', 0.25),
  smoothness: q('keysmooth', 0.15),
  spill: q('keyspill', 0.35),
  matteChoke: q('keychoke', 0),
  matteFeather: q('keymfeather', 0.08),
  edgeFeather: q('portalfeather', 0),
  cornerRadius: q('portalradius', 0),
  debugKey: q('keydebug', 0),
  // How much of the hour's light colour the figure takes on (0 = neutral,
  // 1 = fully the world's light). Grading, not dimming.
  grade: q('portalgrade', 0.55),
  // Share the scene's fog. 1 = exactly what the world gets.
  fog: q('portalfog', 1),
  // Garbage matte, per side (0..1 of the frame).
  cropTop: q('portalcroptop', 0),
  cropRight: q('portalcropright', 0),
  cropBottom: q('portalcropbottom', 0),
  cropLeft: q('portalcropleft', 0),
  cropFeather: q('portalcropfeather', 0.01),
  // Contact shadow.
  shadow: q('portalshadow', 1),
  shadowOpacity: q('portalshadowop', 0.45),
};
if (typeof window !== 'undefined') window.__portal = TUNING;

// ── Built worlds are PERMANENT — so build each one once ──────────────────────
// createWorld generates a world before a single frame streams, which is the
// minute you spend staring at a dead panel. attachWorld reopens a world you
// already built and, in the SDK's words, "resolves immediately for an
// already-ready world". The id is a capability you save; there is no listing.
//
// So the first open of a portal pays the build, and every open after it is
// near-instant — and stops re-paying for a world you already own, since the
// session meter runs during the build too.
//
// Keyed on what the world IS — placement, mode, key colour, and the authored
// destination text — never on the composed prompt.
//
// The prompt is the wrong key: it now carries the hour's lighting ("midday",
// "golden hour", which side the sun is on), so hashing it means the key changes
// as the clock moves and every open after a phase change rebuilds a world we
// already own. Cache on the thing that defines the world, not on the sentence
// describing it.
//
// The consequence, accepted deliberately: a world built at golden hour comes
// back at midnight with that lighting baked in. The shader grade is what tracks
// the clock — see the lighting caveat at the call site.
//
// localStorage keeps this migration-free while it's a test; moving the id onto
// the placement row is what would let every VISITOR skip the build too.
const worldKey = (placementId, mode, identity) => {
  let h = 0;
  for (let i = 0; i < identity.length; i++) h = (Math.imul(31, h) + identity.charCodeAt(i)) | 0;
  return `portal:world:${placementId}:${mode}:${h}`;
};
const recallWorld = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const rememberWorld = (k, id) => { try { localStorage.setItem(k, id); } catch { /* private mode */ } };
const forgetWorld = (k) => { try { localStorage.removeItem(k); } catch { /* noop */ } };

// data: URL → Blob, so the capture can be UPLOADED rather than linked.
function dataUrlToBlob(dataUrl) {
  const [head, body] = dataUrl.split(',');
  const mime = (head.match(/:(.*?);/) || [])[1] || 'image/jpeg';
  const bin = atob(body);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export default function PortalSurface({ placement: p, night = 0 }) {
  const { gl, scene } = useThree();
  const groupRef = useRef();
  const modelRef = useRef(null);
  const videoRef = useRef(null);

  const [phase, setPhase] = useState('idle'); // idle|capturing|building|live|error|denied
  const [note, setNote] = useState('');
  const [tex, setTex] = useState(null);
  // The world's opening still, shown while travel connects. Without it the
  // panel is blank for the whole build — which is what made a slow open feel
  // like a hang rather than a wait.
  const [still, setStill] = useState(null);
  const [mix, setMix] = useState(0); // 0 = wall, 1 = portal
  const isAdmin = authUser().isAdmin;

  // ONE session at a time, enforced by a ref rather than `phase`. State is
  // captured per render, so a handler holding a stale closure can pass a
  // phase check that is already out of date and start a second world — which
  // is a second metered minute, not just a visual glitch. A ref is read live,
  // and it is cleared in exactly one place (teardown).
  const runningRef = useRef(false);
  const expiryRef = useRef(null);

  // The world's clock, same source the street and skyline shade from.
  const tod = useWorldTime(SLICE_DISTRICT.timezone);

  // The video is UNLIT on purpose — it's another world's light, so our sun has
  // no business relighting it — but unlit also meant it ignored dusk entirely
  // and sat there glowing while every texture around it went dark. So the map
  // is multiplied by a day/night tint: full strength by day, dimmed and pulled
  // toward the world's ambient colour at night. It reads as part of the wall
  // again, while still being the brightest thing on it (a doorway should be).
  const mode = portalMode(p);
  const keyColor = (() => {
    const q = qp('portalkeycolor', null);
    if (typeof q === 'string' && /^#?[0-9a-fA-F]{6}$/.test(q)) return q.startsWith('#') ? q : `#${q}`;
    if (typeof q === 'string' && KEY_PRESETS[q]) return KEY_PRESETS[q];
    return KEY_PRESETS.green;
  })();

  // ONE material for both modes — chroma only switches the keying step on. That
  // means the feather/shape layers apply to a seeded portal too, so a wall that
  // opens can also lose its hard rectangular border.
  // Built once and mutated in place: recreating a ShaderMaterial recompiles the
  // program, which would stutter the moment the video appears.
  const portalMat = useMemo(() => makePortalMaterial({
    key: mode === 'chroma',
    keyColor,
    similarity: qp('keysim', 0.4),
    satFloor: qp('keysat', 0.25),
    smoothness: qp('keysmooth', 0.15),
    spill: qp('keyspill', 0.35),
    matteChoke: qp('keychoke', 0),
    matteFeather: qp('keymfeather', 0.08),
    shape: qp('portalshape', 'rect') === 'ellipse' ? SHAPES.ellipse : SHAPES.rect,
    cornerRadius: qp('portalradius', 0),
    // Default OFF so nothing changes look until asked for; 0.15 is a good
    // starting feather for "the video has no edge".
    edgeFeather: qp('portalfeather', 0),
    quadAspect: (p.width || 1) / (p.height || 1),
    debug: qp('keydebug', 0) === 1,
  }), [mode, keyColor, p.width, p.height]);
  useEffect(() => () => portalMat.dispose(), [portalMat]);

  useFrame(() => {
    const night = tod.night ?? 0;

    // GRADE INTO THE HOUR — at every hour, not just after dark.
    //
    // This used to lerp toward white by day, so at midday and golden hour the
    // tint was ~1,1,1 and the figure stayed flat and neutral inside a warm,
    // dusty scene. The light is NORMALISED to unit luminance first, so it
    // shifts hue without darkening; the night dim is applied separately after.
    // That separation is what lets a golden-hour portal go warm without also
    // going dim.
    // ambColor, NOT amb — worldTime's key is ambColor, and reading the wrong
    // one silently yields undefined → white → a grade that does nothing.
    const amb = TMP.set(tod.ambColor || '#ffffff');
    const lum = 0.2126 * amb.r + 0.7152 * amb.g + 0.0722 * amb.b;
    if (lum > 1e-4) amb.multiplyScalar(1 / lum);
    const shade = SHADE.copy(WHITE)
      .lerp(amb, TUNING.grade)                  // how much of the hour's colour she takes
      .multiplyScalar(1 - night * 0.62);        // never fully black — it IS a light source

    // Same air as everything else: copy the scene's OWN fog rather than
    // duplicating its numbers, so it tracks the time-of-day colour and any
    // future change to the fog range without this going stale.
    const f = scene.fog;
    if (f && TUNING.fog > 0) {
      portalMat.uniforms.fogColor.value.copy(f.color);
      portalMat.uniforms.fogNear.value = f.near ?? 80;
      portalMat.uniforms.fogFar.value = f.far ?? 260;
      portalMat.uniforms.fogAmount.value = TUNING.fog;
    } else {
      portalMat.uniforms.fogAmount.value = 0;
    }
    portalMat.uniforms.tint.value.copy(shade);
    portalMat.uniforms.opacity.value = mix;
    // Video wins the moment it exists; until then the world's opening still
    // stands in, keyed by the same stack so the transition is invisible.
    const src = tex || still;
    if (src && portalMat.uniforms.map.value !== src) portalMat.uniforms.map.value = src;

    // Live tuning — pushed every frame so the console handle takes effect
    // mid-clip instead of costing a reload (and a fresh prepaid minute).
    const u = portalMat.uniforms;
    u.crop.value.set(TUNING.cropTop, TUNING.cropRight, TUNING.cropBottom, TUNING.cropLeft);
    u.cropFeather.value = TUNING.cropFeather;
    u.similarity.value = TUNING.similarity;
    u.satFloor.value = TUNING.satFloor;
    u.smoothness.value = TUNING.smoothness;
    u.spill.value = TUNING.spill;
    u.matteChoke.value = TUNING.matteChoke;
    u.matteFeather.value = TUNING.matteFeather;
    u.edgeFeather.value = TUNING.edgeFeather;
    u.cornerRadius.value = TUNING.cornerRadius;
    u.debugKey.value = TUNING.debugKey;
  });

  // Cross-fade in as soon as there is ANYTHING to show — the opening still
  // counts, so the wall reveals the destination during the build rather than
  // holding blank until the stream lands.
  useFrame((_, dt) => {
    if (mix >= 1 || (phase !== 'live' && !still)) return;
    setMix((m) => Math.min(1, m + dt / FADE_SEC));
  });

  const teardown = useCallback(() => {
    // Kill the expiry timer FIRST: a timer left running after a manual or
    // unmount teardown fires into a dead component and can tear down (or
    // re-idle) a session that has since been opened again.
    if (expiryRef.current) { clearTimeout(expiryRef.current); expiryRef.current = null; }
    try { modelRef.current?.disconnect(); } catch { /* already gone */ }
    modelRef.current = null;
    const v = videoRef.current;
    if (v) {
      try { v.pause(); v.removeAttribute('src'); v.load(); document.body.removeChild(v); } catch { /* gone */ }
      videoRef.current = null;
    }
    runningRef.current = false;
    setTex(null);
    setStill(null);
    setMix(0);
  }, []);
  useEffect(() => teardown, [teardown]);

  const open = useCallback(async () => {
    // The live guard. `phase` below is a courtesy check for the UI; THIS is the
    // one that decides, because it can't be stale and it can't be raced by two
    // clicks landing in the same tick.
    if (runningRef.current || modelRef.current) return;
    if (phase !== 'idle' && phase !== 'error') return;
    runningRef.current = true;
    // Metered upstream minutes — the server enforces this too, but refusing
    // here means a visitor's click never even reaches a billable endpoint.
    if (!isAdmin) { runningRef.current = false; setPhase('denied'); setNote('Portals are admin-only while testing'); return; }

    // Is this world already built? The key needs nothing from the server, so we
    // can answer BEFORE asking — and a reused world needs no frame 0 at all.
    // Skipping it saves capturing the wall, base64-ing a JPEG, shipping it, and
    // a GCS write, on every single reopen.
    const key = worldKey(p.placement_id, mode, `${portalDestination(p)}|${keyColor}`);
    const known = recallWorld(key);

    // CHROMA: frame 0 is a flat sheet of the key colour, so the clip literally
    // begins on the colour we're about to key. SEED: frame 0 is the wall.
    let image;
    if (known) {
      image = null; // reopening — frame 0 is already baked into the saved world
    } else if (mode === 'chroma') {
      setPhase('capturing');
      setNote('Opening…');
      image = flatFrame(keyColor);
    } else {
      setPhase('capturing');
      setNote('Reading the wall…');
      image = captureSurface({
        gl,
        scene,
        position: new THREE.Vector3(...p.pos),
        quaternion: new THREE.Quaternion(...p.quat),
        width: p.width,
        height: p.height,
        hide: groupRef.current, // photograph the WALL, not our own quad
      });
    }
    if (!image && !known) { runningRef.current = false; setPhase('error'); setNote('Could not read this surface'); return; }

    setPhase('building');
    setNote('Opening…');
    // Ask the model for the light the world is ACTUALLY in, so the shader has
    // less to correct. Direction comes from the sun's own x position (east in
    // the morning, west in the afternoon), the wording from the hour's phase.
    //
    // CAVEAT, and it's why the shader grade stays the real fix: worlds are
    // cached and reopened via attachWorld, so a world built at golden hour gets
    // reused at midnight with its lighting baked in. This narrows the gap on
    // first build; it cannot track the clock.
    const sunX = tod.keyPos?.[0] ?? 0;
    const lighting = `${tod.phase || 'daylight'}, lit from camera ${sunX >= 0 ? 'right' : 'left'}`;

    const { res, data } = await api.openPortal({
      image,          // null when reopening — the server skips the GCS write
      reuse: !!known,
      mode,
      keyColor,
      lighting,
      destination: portalDestination(p),
      placementId: p.placement_id,
      minutes: 1,
    });
    if (!res.ok) {
      runningRef.current = false;
      setPhase('error');
      setNote(data?.error || 'The portal did not open');
      return;
    }

    // The <video> lives outside React and outside the document flow: it is a
    // texture source, never a visible element. Some browsers refuse to decode a
    // fully detached <video>, so it is attached but parked off-screen.
    const v = document.createElement('video');
    v.playsInline = true; v.muted = true; v.defaultMuted = true; v.autoplay = true;
    v.setAttribute('playsinline', ''); v.setAttribute('muted', '');
    Object.assign(v.style, { position: 'fixed', left: '-9999px', top: '0', width: '2px', height: '2px', opacity: '0', pointerEvents: 'none' });
    document.body.appendChild(v);
    videoRef.current = v;

    try {
      const model = new HappyOysterModel({ mode: 'directing', videoElement: v });
      modelRef.current = model;
      // The build is the slow part (the world is generated before a frame
      // streams) — narrate it from the model's own snapshot rather than
      // guessing, so a long build never reads as a hang.
      model.onWorldState((s) => {
        if (s?.phase === 'creating') setNote('Creating…');
        else if (s?.phase === 'building') setNote('Building what\'s behind it… (first time only)');
        else if (s?.phase === 'failed') { setPhase('error'); setNote('The world failed to build'); }
        // Save the capability the moment it exists — this is what makes every
        // later open skip the build.
        if (s?.encrypted_world_id) rememberWorld(key, s.encrypted_world_id);
        // The model publishes the world's opening still. Showing it the instant
        // it exists means the wall stops being a dead panel while travel
        // negotiates — you see the destination, then it starts moving.
        if (s?.first_frame) {
          loadTexture(s.first_frame).then((t) => { if (t) setStill(t); }).catch(() => {});
        }
        // Did our wall actually become frame 0? The model reports the opening
        // frame it used; if that's empty the world was invented from the prompt
        // alone and the composite will not line up. Worth knowing loudly —
        // silently generating a generic world is the failure mode that looks
        // like success.
        if (s?.phase === 'ready') {
          console.log('[portal] world ready · first_frame =', s.first_frame || '(none — reference NOT used)');
        }
      });
      // A rejected frame 0 (unfetchable URL, bad aspect, content policy) arrives
      // here as an action_error. Without this subscription the model just falls
      // back to generating its own opening frame and we never learn why the
      // reference "didn't take".
      model.onActionError((err) => {
        console.warn('[portal] model rejected an action:', err?.code, err?.message);
        setPhase('error');
        setNote(err?.message || 'The model rejected the surface');
      });
      await model.connect(data.jwt);

      // REUSE if we've built this exact portal before. attachWorld resolves
      // immediately for an already-built world, so this is the difference
      // between a minute of waiting and a couple of seconds.
      let attached = false;
      console.log(known
        ? `[portal] reusing world ${known.slice(0, 12)}… (key ${key})`
        : `[portal] no cached world for ${key} — building once`);
      if (known) {
        setNote('Reopening…');
        try {
          await model.attachWorld(known);
          attached = true;
          console.log('[portal] attached — build skipped');
        } catch (e) {
          // 403001 (not found / not yours) or MODE_MISMATCH: the saved id is
          // stale or belongs to the other mode's model. Drop it and build once
          // more rather than dead-ending on a bad capability.
          console.warn('[portal] cached world unusable, rebuilding:', e?.code || e?.message);
          forgetWorld(key);
        }
      }
      if (!attached) {
        // UPLOAD the capture rather than linking it. With first_frame_image_url,
        // HappyOyster fetches the URL from THEIR servers — our GCS links are
        // signed and time-limited, and if that fetch fails the model quietly
        // builds a world from the prompt alone, which is exactly the "my
        // reference isn't in the result" symptom. The upload path sends the
        // bytes through the Reactor session instead, so nothing has to be
        // reachable from outside. It's also what the playground does.
        //
        // Only on a BUILD: a reopened world already has its frame 0 baked in,
        // and an attach that re-shipped the image would be pure waste.
        if (!image) throw new Error('No opening frame to build this portal from.');
        const blob = dataUrlToBlob(image);
        const issue = await firstFrameImageIssue(blob); // size + 1.5–2.0 landscape, locally
        if (issue) {
          console.warn('[portal] capture rejected before upload:', issue);
          throw new Error(issue);
        }
        setNote('Building what\'s behind it… (first time only)');
        await model.createWorld({
          prompt: data.prompt,
          firstFrameImage: blob,
          resolution: qp('portalres', '720p') === '480p' ? '480p' : '720p',
        });
      }
      setNote('Connecting the stream…');
      await model.startTravel();

      const t = new THREE.VideoTexture(v);
      t.colorSpace = THREE.SRGBColorSpace;
      setTex(t);
      setPhase('live');
      setNote('');
      v.play().catch(() => {});

      // The session is prepaid: close it on the clock we bought, not whenever.
      // Held in a ref so teardown can cancel it — an orphaned timer firing into
      // a later session is how one click turns into two billed minutes.
      expiryRef.current = setTimeout(() => {
        expiryRef.current = null;
        teardown();
        setPhase('idle');
      }, (data.sessionSeconds || 60) * 1000);
    } catch (e) {
      console.warn('[portal] session failed:', e);
      teardown();
      setPhase('error');
      setNote(e?.message || 'The portal did not open');
    }
  }, [phase, isAdmin, gl, scene, p, teardown]);

  const w = p.width, h = p.height;
  const glow = 0.15 + night * 0.5;

  return (
    <group ref={groupRef} position={p.pos} quaternion={p.quat} userData={{ isPlacement: true, isPortal: true }}>
      {/* The portal plane. Before it opens this is a near-invisible sheen over
          the wall — a portal shouldn't advertise itself as a screen; the wall
          should just be a wall until it isn't. */}
      <mesh
        position={[0, 0, 0.02]}
        onClick={(e) => { e.stopPropagation(); open(); }}
        onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { document.body.style.cursor = 'auto'; }}
      >
        <planeGeometry args={[w, h]} />
        {/* The effect stack, attached rather than declared — created once and
            mutated, so the program never recompiles mid-clip. Also carries the
            opening still, so the destination is on the wall before the stream
            arrives. */}
        {tex || still ? (
          <primitive object={portalMat} attach="material" />
        ) : (
          <meshBasicMaterial
            key="portal-idle"
            color="#7dd3ff"
            transparent
            opacity={phase === 'idle' ? glow * 0.12 : 0.22}
            depthWrite={false}
          />
        )}
      </mesh>

      {/* CONTACT SHADOW — the thing that makes a keyed figure read as standing
          there rather than pasted on. Two parts, both only while the portal is
          actually showing something:
            caster  — an invisible capsule where the figure would be, throwing a
                      real shadow from the world's own sun. colorWrite AND
                      depthWrite are off: with depth on it would be an invisible
                      OCCLUDER, punching a hole in whatever it covers.
            catcher — a small ground patch that receives it. Needed because
                      nothing in the model city sets receiveShadow (only OsmCity,
                      the Park and interiors do), so a shadow cast onto the
                      street would land on nothing. ShadowMaterial is invisible
                      except where shadowed, so this adds no visible geometry.
          Both sit in the quad's local space, pushed out along its normal, so
          they follow the wall's orientation for free. */}
      {TUNING.shadow > 0 && (tex || still) && (
        <group position={[0, -h / 2, 0.35]}>
          <mesh castShadow position={[0, h * 0.42, 0]}>
            <capsuleGeometry args={[Math.max(0.12, w * 0.06), h * 0.62, 4, 8]} />
            <meshBasicMaterial colorWrite={false} depthWrite={false} />
          </mesh>
          <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
            <planeGeometry args={[w * 1.2, w * 1.2]} />
            <shadowMaterial transparent opacity={TUNING.shadowOpacity} depthWrite={false} />
          </mesh>
        </group>
      )}

      {/* Status only while something is happening — a live portal carries no UI. */}
      {note && phase !== 'live' && (
        <Text
          position={[0, 0, 0.06]}
          fontSize={Math.max(0.12, h * 0.08)}
          color={phase === 'error' || phase === 'denied' ? '#ff9d9d' : '#cfeaff'}
          anchorX="center"
          anchorY="middle"
          maxWidth={w * 0.9}
          outlineWidth={0.004}
          outlineColor="#000"
        >
          {note}
        </Text>
      )}
    </group>
  );
}
